/* =====================================================================
 * Storage — 공통 styled-components
 * ===================================================================== */

import styled, { css, keyframes } from 'styled-components';

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

export const StorageContainer = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  width: calc(100% - 140px);
  height: calc(100% - 120px);
  max-width: 1100px;
  max-height: 740px;
  margin: auto;
  overflow: hidden;
  border-radius: 16px;
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(30,30,32,0.82)'
      : 'rgba(255,255,255,0.78)'};
  backdrop-filter: blur(32px) saturate(180%);
  -webkit-backdrop-filter: blur(32px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 24px 80px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)'
      : '0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08)'};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: calc(100% - 32px);
    height: calc(100% - 32px);
    margin: 16px;
    border-radius: 14px;
  }
`;

export const SlideTrack = styled.div<{ $showContent?: boolean }>`
  display: flex;
  flex: 1;
  min-height: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: none;
    width: 200%;
    height: 100%;
    transform: translateX(${({ $showContent }) => ($showContent ? '-50%' : '0')});
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

export const SidebarPane = styled.aside<{ $hidden?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ theme }) => theme.dm.listWidth};
  min-width: ${({ theme }) => theme.dm.listMinWidth};
  max-width: ${({ theme }) => theme.dm.listMaxWidth};
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-right: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(0,0,0,0.02)'};
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  flex-shrink: 0;

  ${({ $hidden }) => $hidden && 'display: none;'}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
    width: 50%;
    min-width: 50%;
    max-width: 50%;
    border-right: none;
  }
`;

export const MainPane = styled.main`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: transparent;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 50%;
    min-width: 50%;
    flex: none;
  }
`;

/* ------------------------------------------------------------------ */
/* Sidebar pieces                                                      */
/* ------------------------------------------------------------------ */

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  padding: ${({ theme }) => theme.sidebar.headerPad};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.sidebarMobile?.headerHeight ?? '40px'};
    padding: ${({ theme }) => theme.sidebarMobile?.headerPad ?? '8px 10px'};
  }
`;

export const SidebarHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const SidebarTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8125rem;
  }
`;

export const MainSidebarOpenBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const SidebarToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

/* ---- Mode avatar (sidebar workspace sections) ---- */

export const ModeAvatarWrap = styled.span`
  position: relative;
  width: 22px;
  height: 22px;
  min-width: 22px;
  flex-shrink: 0;
`;

export const ModeAvatarCircle = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e'};
  font-weight: 700;
  font-size: 9px;
  line-height: 1;
  user-select: none;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275),
    box-shadow 0.3s ease;

  svg {
    color: inherit;
    flex-shrink: 0;
  }
`;

export const TeamFolderAvatar = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,200,50,0.15)'
      : 'rgba(255,180,0,0.12)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#ffc832' : '#e8a000'};
  flex-shrink: 0;
  user-select: none;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275),
    box-shadow 0.3s ease;

  svg {
    color: inherit;
    flex-shrink: 0;
  }
`;

export const SkillAvatar = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,149,0,0.15)'
      : 'rgba(255,149,0,0.10)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#ffb347' : '#e68a00'};
  flex-shrink: 0;
  user-select: none;
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275),
    box-shadow 0.3s ease;

  svg {
    color: inherit;
    flex-shrink: 0;
  }
`;

export const ModeAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

export const ModeAvatarDot = styled.span<{ $color: string }>`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 1.5px solid ${({ theme }) => theme.colors.bgSidebar};
`;

export const SidebarTreeWrap = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.dm.listPad};

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.dmMobile?.listPad ?? '0 6px'};
  }
`;

export const SidebarFooter = styled.div`
  flex-shrink: 0;
  padding: 8px;
  margin: 4px 8px 8px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255,255,255,0.04)'
      : 'rgba(255,255,255,0.6)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 0 0 0.5px rgba(255,255,255,0.06)'
      : '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.04)'};
`;

/* ---- Tree node ---- */

export const TreeNode = styled.button<{
  $depth: number;
  $active?: boolean;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.bgHover : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  padding: 6px 8px 6px ${({ $depth }) => 8 + $depth * 16}px;
  border-radius: 6px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  text-align: left;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const TreeNodeLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ---- Usage bar ---- */

export const UsageBarOuter = styled.div`
  height: 3px;
  border-radius: 1.5px;
  background: ${({ theme }) => theme.colors.bgTertiary};
  box-shadow: inset 0 0.5px 1px rgba(0,0,0,0.06);
  overflow: hidden;
`;

export const UsageBarInner = styled.div<{ $ratio: number }>`
  height: 100%;
  border-radius: 1.5px;
  width: ${({ $ratio }) => Math.min($ratio * 100, 100)}%;
  background: ${({ $ratio, theme }) =>
    $ratio >= 0.9
      ? theme.colors.usageBarCritical
      : $ratio >= 0.7
        ? theme.colors.usageBarCaution
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.22)'
          : 'rgba(0,0,0,0.18)'};
  transition: width ${({ theme }) => theme.transitions.normal};
`;

export const UsageLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 4px;
  display: block;
`;

/* ------------------------------------------------------------------ */
/* Toolbar                                                             */
/* ------------------------------------------------------------------ */

export const ToolbarWrap = styled.div<{ $sidebarCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  padding: 0 20px;
  padding-left: ${({ $sidebarCollapsed }) => ($sidebarCollapsed ? '48px' : '20px')};
  flex-shrink: 0;
  border-bottom: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
  transition: padding-left ${({ theme }) => theme.transitions.fast};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.sidebarMobile?.headerHeight ?? '48px'};
    padding-left: 20px;
  }
`;

export const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
`;

export const BreadcrumbItem = styled.button<{ $isLast?: boolean }>`
  border: none;
  background: none;
  padding: 2px 4px;
  border-radius: 4px;
  color: ${({ $isLast, theme }) =>
    $isLast ? theme.colors.textPrimary : theme.colors.textMuted};
  font-weight: ${({ $isLast }) => ($isLast ? 600 : 400)};
  cursor: ${({ $isLast }) => ($isLast ? 'default' : 'pointer')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  font-size: inherit;

  &:hover:not(:disabled) {
    ${({ $isLast, theme }) =>
      !$isLast &&
      css`
        background: ${theme.colors.bgHover};
        color: ${theme.colors.textPrimary};
      `}
  }
`;

export const BreadcrumbSep = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  flex-shrink: 0;
`;

export const SearchInput = styled.input`
  height: 32px;
  width: 180px;
  padding: 0 10px 0 30px;
  font-size: 0.8125rem;
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 2px
      ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,149,0,0.18)' : 'rgba(0,0,0,0.06)'};
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 8px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`;

export const IconBtn = styled.button<{ $active?: boolean }>`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.bgHover : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ------------------------------------------------------------------ */
/* File Grid                                                           */
/* ------------------------------------------------------------------ */

export const GridContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

/* ---- File Card ---- */

export const StorageCard = styled.div<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  border: 0.5px solid
    ${({ $selected, theme }) =>
      $selected
        ? theme.colors.accent
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) => theme.colors.bgSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  overflow: hidden;
  user-select: none;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-color: ${({ $selected, theme }) =>
      $selected
        ? theme.colors.accent
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.12)'
          : 'rgba(0,0,0,0.1)'};
  }
`;

export const CardPreview = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  position: relative;
  overflow: hidden;
`;

export const CardThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const CardVideoThumbnail = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

export const CardIconWrap = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $color }) => $color ?? 'rgba(0,0,0,0.06)'};
  color: #fff;
`;

export const StorageCardBody = styled.div`
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StorageCardTitle = styled.span`
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Checkbox ---- */

export const CheckBox = styled.div<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.accent : theme.colors.border};
  background: ${({ $checked, theme }) =>
    $checked ? theme.colors.accent : 'transparent'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textInverse};
`;

/* ------------------------------------------------------------------ */
/* File List (table)                                                   */
/* ------------------------------------------------------------------ */

export const ListContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 20px 16px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(34,34,36,0.88)' : 'rgba(255,255,255,0.88)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
`;

export const Th = styled.th<{ $sortable?: boolean }>`
  text-align: left;
  padding: 10px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  white-space: nowrap;
  user-select: none;

  &:hover {
    ${({ $sortable, theme }) =>
      $sortable &&
      css`
        color: ${theme.colors.textPrimary};
      `}
  }
`;

export const Tr = styled.tr<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.accentLight : 'transparent'};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.accentLight : theme.colors.bgHover};
  }
`;

export const Td = styled.td`
  padding: 8px 12px;
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  white-space: nowrap;
`;

export const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const NameText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${Tr}:hover & {
    opacity: 1;
  }
`;

/**
 * 출처 컬럼의 "바로가기" 아이콘 버튼.
 * disabled 시(=원본 페이지를 알 수 없음) 회색 + non-clickable.
 */
export const SourceLinkButton = styled.button`
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: default;
    color: ${({ theme }) => theme.colors.textMuted};
    opacity: 0.4;
  }
`;

/* ------------------------------------------------------------------ */
/* Empty / Drop state                                                  */
/* ------------------------------------------------------------------ */

const pulse = keyframes`
  0%, 100% { border-color: ${css`var(--drop-color, currentColor)`}; opacity: 0.6; }
  50% { opacity: 1; }
`;

export const EmptyWrap = styled.div<{ $isDragging?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 24px;
  min-height: 300px;

  ${({ $isDragging, theme }) =>
    $isDragging &&
    css`
      --drop-color: ${theme.colors.accent};
      border: 2px dashed ${theme.colors.accent};
      border-radius: ${theme.sizes.borderRadius};
      margin: 16px 20px;
      background: ${theme.colors.accentLight};
      animation: ${pulse} 1.4s ease-in-out infinite;
    `}
`;

export const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))'
      : 'linear-gradient(145deg, rgba(0,0,0,0.04), rgba(0,0,0,0.02))'};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const EmptyTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const EmptySub = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  line-height: 1.5;
`;

/* ------------------------------------------------------------------ */
/* Rename inline input                                                 */
/* ------------------------------------------------------------------ */

export const RenameInput = styled.input`
  width: 100%;
  padding: 4px 6px;
  border: 0.5px solid ${({ theme }) => theme.colors.accent};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgInput};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  outline: none;
  box-shadow: 0 0 0 2px
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,149,0,0.18)' : 'rgba(0,0,0,0.06)'};
`;

/* ------------------------------------------------------------------ */
/* Sidebar sections (collapsible workspace groups)                     */
/* ------------------------------------------------------------------ */

export const SectionDivider = styled.div`
  height: 0;
  margin: 4px 0;
`;

export const SidebarSection = styled.div`
  margin-bottom: 2px;
`;

export const SidebarSectionHeader = styled.button<{ $open?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 10px 10px 4px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 8px 8px 4px;
    font-size: 0.6875rem;
  }
`;

export const SectionIcon = styled.span<{ $color?: string }>`
  display: flex;
  align-items: center;
  color: ${({ $color }) => $color ?? 'currentColor'};
`;

export const SectionLabel = styled.span`
  flex: 1;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8125rem;
  }
`;

export const SectionChevron = styled.span<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: transform ${({ theme }) => theme.transitions.fast};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

export const SectionBadge = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
`;

export const SectionAddBtn = styled.button`
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const TreeNodeActions = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 100ms;
`;

export const TreeNodeRow = styled.button<{
  $depth: number;
  $active?: boolean;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ $depth }) => `6px 8px 6px ${8 + $depth * 14}px`};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.sidebar.itemFontSize};
  text-align: left;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  &:hover ${ModeAvatarCircle},
  &:hover ${TeamFolderAvatar} {
    transform: scale(1.12) rotate(-6deg);
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 2px 6px rgba(0,0,0,0.45)'
        : '0 2px 6px rgba(0,0,0,0.12)'};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : '#ffffff'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)'};
      font-weight: 600;
    `}

  &:hover ${TreeNodeActions} {
    opacity: 1;
  }
`;

export const TreeNodeActionBtn = styled.button<{ $danger?: boolean }>`
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $danger }) =>
    $danger ? theme.colors.error : theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme, $danger }) =>
      $danger ? theme.colors.error : theme.colors.textPrimary};
  }
`;

export const EmptyHint = styled.div`
  padding: 12px 16px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`;

/* ------------------------------------------------------------------ */
/* Page preview card                                                   */
/* ------------------------------------------------------------------ */

export const PagePreviewWrap = styled.div`
  border: 0.5px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  background: ${({ theme }) => theme.colors.bgSecondary};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const PagePreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PagePreviewIconWrap = styled.div<{ $color?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => $color ?? 'rgba(0,0,0,0.06)'};
  color: #fff;
  flex-shrink: 0;
`;

export const PagePreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PagePreviewTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PagePreviewMeta = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const PagePreviewActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

export const AttachmentDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};

  &::after {
    content: '';
    flex: 1;
    height: 0.5px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  }
`;

/* ------------------------------------------------------------------ */
/* Page card (grid view — workspace page as folder-like card)          */
/* ------------------------------------------------------------------ */

export const PageCardBadge = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #fff;
  background: rgba(0, 0, 0, 0.45);
`;
